/**
 * Task D6 — the account profile and the estate statistics (routes 1 and 2).
 *
 * Two independent queries rather than one, and **each section degrades on its
 * own**. If the upstream can answer for the profile but not for the counters,
 * the page shows the profile and an error where the counters go — a screen that
 * blanks entirely because one of two calls failed tells the operator nothing
 * about which half is broken.
 *
 * The APN is rendered verbatim and unabridged. It is what a customer types into
 * a device, so §05.7 keeps it identical under both attribution modes and §7
 * keeps it out of any masking or truncation.
 */

import type { AccountStats } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { ErrorState } from '../../components/ErrorState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Skeleton } from '../../components/Skeleton.js';
import { apiRequest } from '../../lib/apiClient.js';

import { SecuritySection } from './SecuritySection.js';
import { SessionsPanel } from './SessionsPanel.js';
import { TotpRequiredBanner } from './TotpRequiredBanner.js';

/**
 * The shape `GET /account/profile` returns.
 *
 * Declared here rather than imported because `@rd/shared` carries `AccountStats`
 * but has no profile DTO yet — the API's own type lives in
 * the API's own domain types as `DomainAccountProfile`, which
 * the client cannot import across the app boundary. Every field below is
 * transcribed from it. Promoting it into the contract package is the right fix
 * and is noted for whoever owns `packages/shared/src/dto.ts`.
 */
interface AccountProfile {
  customerName: string | null;
  customerType: string | null;
  accountType: string | null;
  status: string | null;
  address: string | null;
  billingAddress: string | null;
  emailAddress: string | null;
  primaryPhoneNumber: string | null;
  alternatePhoneNumber: string | null;
  billCycle: string | null;
  apn: string | null;
  coordinatorName: string | null;
  coordinatorEmailId: string | null;
  coordinatorMobile: string | null;
  createdOn: string | null;
  preferredLanguage: string | null;
}

export function AccountPage(): ReactElement {
  const profile = useQuery<AccountProfile>({
    queryKey: ['account', 'profile'],
    queryFn: () => apiRequest<AccountProfile>('/account/profile'),
  });

  const stats = useQuery<AccountStats>({
    queryKey: ['account', 'stats'],
    queryFn: () => apiRequest<AccountStats>('/account/stats'),
  });

  return (
    <>
      <PageHeader title="Account" description="Who this account belongs to, and what is in it." />

      {/* §B7 soft enforcement: the banner and the section it points at share
          this screen, so "set it up now" lands the reader beside the button. */}
      <TotpRequiredBanner />

      <section aria-labelledby="account-stats-heading" className="mb-10">
        <h2 id="account-stats-heading" className="mb-3 text-sm font-semibold text-body">
          The estate
        </h2>

        {stats.isPending ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : null}

        {stats.isError ? (
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        ) : null}

        {stats.data ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Baskets" value={stats.data.totalBaskets} />
            <Stat label="SIMs" value={stats.data.totalSims} />
            <Stat label="Active" value={stats.data.activeSims} />
            <Stat label="Available" value={stats.data.availableSims} />
            <Stat label="In test mode" value={stats.data.testModeSims} />
            <Stat label="In safe custody" value={stats.data.safeCustodySims} />
            <Stat label="Suspended" value={stats.data.suspendedSims} />
            <Stat label="Disconnected" value={stats.data.tempDisconnectedSims} />
            <Stat label="Inactive" value={stats.data.inactiveSims} />
            <Stat label="In progress" value={stats.data.inProgressSims} />
          </div>
        ) : null}
      </section>

      {/* §B7/§B6 — the caller's own security posture and live sessions. These
          are about the signed-in *user*, unlike everything above, which is
          about the account; they sit between the estate and the static profile
          so the actionable sections stay above the reference one. */}
      <SecuritySection />
      <SessionsPanel />

      <section aria-labelledby="account-profile-heading">
        <h2 id="account-profile-heading" className="mb-3 text-sm font-semibold text-body">
          Account details
        </h2>

        {profile.isPending ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : null}

        {profile.isError ? (
          <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
        ) : null}

        {profile.data ? (
          <dl className="grid gap-x-8 gap-y-4 rounded-[var(--brand-radius)] border border-line bg-surface p-6 sm:grid-cols-2">
            <Detail label="Account name" value={profile.data.customerName} />
            <Detail label="Account status" value={profile.data.status} />
            <Detail label="Account type" value={profile.data.accountType} />
            <Detail label="Customer type" value={profile.data.customerType} />
            <Detail label="Billing cycle" value={profile.data.billCycle} />
            <Detail label="Opened" value={profile.data.createdOn} />
            <Detail label="Email" value={profile.data.emailAddress} />
            <Detail label="Phone" value={profile.data.primaryPhoneNumber} />
            <Detail label="Alternate phone" value={profile.data.alternatePhoneNumber} />
            <Detail label="Preferred language" value={profile.data.preferredLanguage} />
            <Detail label="Address" value={profile.data.address} />
            <Detail label="Billing address" value={profile.data.billingAddress} />
            <Detail label="Coordinator" value={profile.data.coordinatorName} />
            <Detail label="Coordinator email" value={profile.data.coordinatorEmailId} />
            <Detail label="Coordinator phone" value={profile.data.coordinatorMobile} />
            {/* Typed into a device by a customer, so it is exact and complete. */}
            <Detail label="APN" value={profile.data.apn} mono />
          </dl>
        ) : null}
      </section>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number | null }): ReactElement {
  return (
    <div className="rounded-[var(--brand-radius)] border border-line bg-surface p-4">
      <p className="text-sm text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-body">
        {value === null ? '—' : value.toLocaleString()}
      </p>
    </div>
  );
}

function Detail({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={`text-sm text-body ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</dd>
    </div>
  );
}
